/**
 * Diagnostic for the intermittent white screen on the shared-trip page.
 *
 * Once the trip has rendered, checks that the browser draws a frame with the
 * trip header in it within 5 s, and that the main thread stays responsive for
 * that window. If not, sends one Sentry warning per page load carrying the
 * long tasks and enough device context to tell a stalled compositor from a
 * blocked main thread. No personal data: the SDK runs with sendDefaultPii false.
 *
 * A tab that freezes for good never runs that check, so the page also keeps a
 * marker in localStorage until it has drawn something; the next app load
 * reports a marker its tab never got to clear.
 */

import { reportError } from './reportError';
import { safeStorage } from './safeStorage';

const STALL_MS = 5000;
// After a late check, how long to wait for a frame before calling it "noFrame".
const FRAME_GRACE_MS = 1000;
// A visible tab's 5 s timer firing this late means the main thread was busy.
const BLOCKED_MS = 2000;

interface LoafScript {
  invoker?: string;
  sourceURL?: string;
  sourceFunctionName?: string;
  duration: number;
}

interface LongAnimationFrame extends PerformanceEntry {
  blockingDuration?: number;
  styleAndLayoutStart?: number;
  scripts?: LoafScript[];
}

let reported = false;

const MARKER_KEY = 'tb_shared_trip_unpainted';

interface UnpaintedMarker {
  path: string;
  writtenAt: number;
  pageMsAtWrite: number;
  placeCount: number | null;
  tripRenderStartedMs: number | null;
  hiddenAtMs: number | null;
  viewport: string;
  userAgent: string;
  theme: string | null;
}

let marker: UnpaintedMarker | null = null;

const round = (n: number) => Math.round(n);

function record(type: string) {
  const entries: PerformanceEntry[] = [];
  let observer: PerformanceObserver | null = null;
  if (typeof PerformanceObserver !== 'undefined' && PerformanceObserver.supportedEntryTypes?.includes(type)) {
    observer = new PerformanceObserver((list) => { entries.push(...list.getEntries()); });
    observer.observe({ type, buffered: true });
  }
  return {
    entries,
    flush: () => { if (observer) entries.push(...observer.takeRecords()); },
    stop: () => observer?.disconnect(),
  };
}

function longest<T extends { duration: number }>(entries: T[], n: number): T[] {
  return [...entries].sort((a, b) => b.duration - a.duration).slice(0, n);
}

// Sentry collapses extras nested deeper than 3 levels, so entries go out as short strings.
function describeLongTask(e: PerformanceEntry): string {
  return `start ${round(e.startTime)}ms, ${round(e.duration)}ms`;
}

function describeAnimationFrame(e: LongAnimationFrame): string {
  const parts = [`start ${round(e.startTime)}ms, ${round(e.duration)}ms`];
  if (e.blockingDuration !== undefined) parts.push(`blocking ${round(e.blockingDuration)}ms`);
  if (e.styleAndLayoutStart) parts.push(`style+layout ${round(e.startTime + e.duration - e.styleAndLayoutStart)}ms`);
  const script = longest(e.scripts ?? [], 1)[0];
  if (script) {
    const file = script.sourceURL?.split('/').pop() || 'unknown';
    parts.push(`top script ${round(script.duration)}ms ${script.invoker ?? ''} ${file}#${script.sourceFunctionName || '?'}`);
  }
  return parts.join(', ');
}

function paintTime(name: string): number | null {
  const entry = performance.getEntriesByType('paint').find((e) => e.name === name);
  return entry ? round(entry.startTime) : null;
}

const wait = (ms: number) => new Promise<void>((resolve) => { setTimeout(resolve, ms); });

// The second callback runs only after the first frame has been drawn.
function afterNextFrame(): Promise<void> {
  return new Promise((resolve) => {
    requestAnimationFrame(() => requestAnimationFrame(() => resolve()));
  });
}

function onMarkerPageHidden() {
  if (marker && document.visibilityState !== 'visible' && marker.hiddenAtMs === null) {
    marker.hiddenAtMs = round(performance.now());
    safeStorage.setJSON(MARKER_KEY, marker);
  }
}

/** Call when the page starts loading a trip. */
export function markSharedTripUnpainted(): void {
  if (!marker) {
    // A tab still able to run script clears the marker as it unloads; a frozen one cannot.
    window.addEventListener('pagehide', clearSharedTripMarker);
    document.addEventListener('visibilitychange', onMarkerPageHidden);
  }
  marker = {
    path: window.location.pathname,
    writtenAt: Date.now(),
    pageMsAtWrite: round(performance.now()),
    placeCount: null,
    tripRenderStartedMs: null,
    hiddenAtMs: null,
    viewport: `${window.innerWidth}x${window.innerHeight} @${window.devicePixelRatio}x`,
    userAgent: navigator.userAgent,
    theme: document.documentElement.getAttribute('data-theme'),
  };
  safeStorage.setJSON(MARKER_KEY, marker);
}

/** Call once the page has drawn its trip, or its not-found or error screen. */
export function clearSharedTripMarker(): void {
  if (!marker) return;
  marker = null;
  window.removeEventListener('pagehide', clearSharedTripMarker);
  document.removeEventListener('visibilitychange', onMarkerPageHidden);
  safeStorage.remove(MARKER_KEY);
}

/** Clears the marker after the next drawn frame; returns a cancel for the effect. */
export function clearSharedTripMarkerAfterNextFrame(): () => void {
  let cancelled = false;
  void afterNextFrame().then(() => { if (!cancelled) clearSharedTripMarker(); });
  return () => { cancelled = true; };
}

/**
 * Call once at app start, before any page renders. Reports a marker left by a
 * shared-trip load whose tab never drew and never unloaded cleanly. A marker
 * younger than 5 s may belong to a load still in flight in another tab.
 */
export function reportUnpaintedSharedTripLoad(): void {
  const previous = safeStorage.getJSON<UnpaintedMarker>(MARKER_KEY);
  if (!previous) return;
  const ageMs = Date.now() - previous.writtenAt;
  if (ageMs < STALL_MS) return;
  safeStorage.remove(MARKER_KEY);
  reportError(
    'sharedTrip.paintStallPreviousLoad',
    new Error('A shared-trip load never drew and never unloaded cleanly'),
    { ...previous, ageMs },
    { level: 'warning', fingerprint: ['sharedTrip.paintStallPreviousLoad'] },
  );
}

/**
 * Call from a layout effect once the trip has committed; returns the cleanup.
 * `renderStartedAt` is when the first render with the trip began, so a slow
 * render counts against the 5 s. `getHeader` is read at check time.
 */
export function watchSharedTripPaint(
  getHeader: () => HTMLElement | null,
  placeCount: number,
  renderStartedAt: number,
): () => void {
  if (reported) return () => {};
  if (marker) {
    marker.placeCount = placeCount;
    marker.tripRenderStartedMs = round(renderStartedAt);
    safeStorage.setJSON(MARKER_KEY, marker);
  }

  const longTasks = record('longtask');
  const animationFrames = record('long-animation-frame');
  const waitedForVisible = document.visibilityState !== 'visible';
  const renderMs = performance.now() - renderStartedAt;

  let stopped = false;
  let armedAt: number | null = null;
  let frameAt: number | null = null;
  let firstFrame: Promise<void> = Promise.resolve();
  let hiddenDuringWindow = false;
  let timer: ReturnType<typeof setTimeout> | undefined;

  const stop = () => {
    stopped = true;
    clearTimeout(timer);
    document.removeEventListener('visibilitychange', onVisibilityChange);
    longTasks.stop();
    animationFrames.stop();
  };

  const check = async () => {
    if (stopped || armedAt === null) return;
    const checkDelay = performance.now() - armedAt;
    // A blocked main thread delays this timer and the frame alike: give a recovering page one more chance to draw.
    if (frameAt === null) {
      await Promise.race([firstFrame, wait(FRAME_GRACE_MS)]);
    }
    if (stopped) return;

    const header = getHeader();
    const headerHeight = header?.isConnected ? header.getBoundingClientRect().height : 0;
    const frameGap = frameAt === null ? null : frameAt - armedAt;
    longTasks.flush();
    // A stall while the data was in flight ends before the trip renders, so only the long-task log shows it.
    const longestBeforeRender = Math.max(0, ...longTasks.entries.filter((e) => e.startTime < renderStartedAt).map((e) => e.duration));
    const paintState =
      frameGap === null ? 'noFrame'
        : frameGap > STALL_MS ? 'lateFrame'
          : headerHeight === 0 ? 'headerNotVisible'
            : !hiddenDuringWindow && checkDelay - STALL_MS > BLOCKED_MS ? 'blockedAfterPaint'
              : longestBeforeRender > STALL_MS ? 'blockedBeforeRender'
                : null;
    if (!paintState || reported) {
      stop();
      return;
    }
    // Chrome logs a long animation frame only once that frame has ended, so let the stalled one finish first.
    await Promise.race([afterNextFrame(), wait(FRAME_GRACE_MS)]);
    if (stopped || reported) return;
    longTasks.flush();
    animationFrames.flush();
    stop();
    reported = true;

    reportError(
      'sharedTrip.paintStall',
      new Error(`Shared trip not drawn cleanly within ${STALL_MS / 1000}s of its data: ${paintState}`),
      {
        paintState,
        placeCount,
        armedAtMs: round(armedAt),
        renderMs: round(renderMs),
        longestTaskBeforeRenderMs: round(longestBeforeRender),
        frameGapMs: frameGap === null ? null : round(frameGap),
        checkDelayMs: round(checkDelay),
        waitedForVisible,
        hiddenDuringWindow,
        visibilityState: document.visibilityState,
        headerInDom: Boolean(header?.isConnected),
        headerHeight: round(headerHeight),
        firstPaintMs: paintTime('first-paint'),
        firstContentfulPaintMs: paintTime('first-contentful-paint'),
        longTaskCount: longTasks.entries.length,
        longTaskTotalMs: round(longTasks.entries.reduce((sum, e) => sum + e.duration, 0)),
        longTasks: longest(longTasks.entries, 5).map(describeLongTask),
        longAnimationFrames: longest(animationFrames.entries as LongAnimationFrame[], 3).map(describeAnimationFrame),
        viewport: `${window.innerWidth}x${window.innerHeight} @${window.devicePixelRatio}x`,
        userAgent: navigator.userAgent,
        theme: document.documentElement.getAttribute('data-theme'),
        bodyBackground: getComputedStyle(document.body).backgroundColor,
      },
      { level: 'warning', fingerprint: ['sharedTrip.paintStall'] },
    );
  };

  const arm = (from: number) => {
    armedAt = from;
    firstFrame = afterNextFrame().then(() => {
      frameAt = performance.now();
      clearSharedTripMarker();
    });
    timer = setTimeout(() => { void check(); }, Math.max(0, armedAt + STALL_MS - performance.now()));
  };

  function onVisibilityChange() {
    if (document.visibilityState !== 'visible') {
      if (armedAt !== null) hiddenDuringWindow = true;
    } else if (armedAt === null) {
      arm(performance.now());
    }
  }

  document.addEventListener('visibilitychange', onVisibilityChange);
  // A trip opened in a background tab draws nothing until it is shown, so the clock starts then.
  if (!waitedForVisible) arm(renderStartedAt);

  return stop;
}
